/**
 * Median.co Native Bridge
 * Pont unifié pour les APIs natives Median.co
 */

import Median from 'median-js-bridge';
import {
  clearPendingPush,
  extractInternalActionUrl,
  readAndClearPendingPush,
} from '@/utils/pushOpenBuffer';

// ============== Health Bridge Types ==============

export type HealthDataType =
  | 'steps'
  | 'distance'
  | 'activeEnergy'
  | 'exerciseTime'
  | 'sleep'
  // Extended types added 2026-04-11 alongside the KokenHealthKit native
  // bridge. Each maps to a distinct HKObjectType on the Swift side.
  | 'heartRate'
  | 'mindful'
  | 'stand'
  | 'flightsClimbed'
  // Legacy Median HealthBridge types — only reachable via the paid
  // fallback path, not supported by KokenHealthKit natively.
  // Types ci-dessous : héritage historique du type HealthDataType. Ils ne
  // sont PAS supportés par notre plugin custom KokenHealthKit (iOS) ni par
  // le futur plugin Android. Koken ne souscrit PAS au plugin payant
  // Median HealthBridge, donc ces types ne remontent jamais en prod.
  // Conservés pour ne pas casser d'éventuels consumers legacy.
  | 'height'
  | 'weight'
  | 'bmi'
  | 'calorieIntake'
  | 'waterIntake';

export interface HealthBridgeDataPoint {
  start: string;  // ISO 8601
  end: string;    // ISO 8601
  value: number;
}

export interface HealthBridgeResponse {
  data: {
    [key in HealthDataType]?: HealthBridgeDataPoint[];
  };
}

export interface HealthPermissionsResult {
  granted: HealthDataType[];
  declined: HealthDataType[];
}

// ============== KokenHealth Bridge Types (Koken custom plugins) ==============
// Koken ne souscrit PAS au plugin payant Median HealthBridge. Toute la
// plomberie santé repose sur des plugins natifs custom :
//   - iOS : KokenHealthKit.swift → window.kokenHealth (actif)
//   - Android : module Kotlin Santé Connect (dépôt de la coque koken-ios-build,
//     android/app/src/main/java/com/koken/healthkit/). Son shim
//     KokenHealthKitBridge.js expose window.kokenHealth avec la MÊME API qu'iOS ;
//     window.kokenHealthAndroid n'est que le canal natif (postMessage) du shim.
//     Reste à publier sur le Play Store et à constater une première
//     synchronisation réelle.
// Le fallback `window.median.healthBridge` mentionné dans certaines fonctions
// est du code défensif inactif — il ne s'active que si on souscrit un jour
// au plugin payant Median.

/**
 * Source tag attached to every kokenHealth response. Critical for the
 * anti-cheat layer: the server refuses a `smartwatch` validation unless
 * the data's source is `apple_watch` (or `wear_os` on Android). Values:
 *
 * - `apple_watch` — sample originated on the paired Apple Watch
 * - `iphone` — sample originated on the iPhone (podometer, built-in)
 * - `wear_os` — Android counterpart
 * - `third_party:<bundleId>` — Strava, Fitbit, MyFitnessPal, etc.
 * - `unknown` — no samples matched (zero data) or heuristic failed
 */
export type KokenHealthSource =
  | 'apple_watch'
  | 'iphone'
  | 'wear_os'
  | 'health_connect'
  | 'manual'
  | 'unknown'
  | `third_party:${string}`
  | `health_connect:${string}`;

interface KokenHealthSourced {
  source: KokenHealthSource;
}

export interface KokenHealthStepsToday extends KokenHealthSourced {
  steps: number;
  startDate: string;
  endDate: string;
}

export interface KokenHealthStepsRangePoint {
  date: string;
  steps: number;
}

export interface KokenHealthRangeResult extends KokenHealthSourced {
  points: KokenHealthStepsRangePoint[];
}

export interface KokenHealthDistanceToday extends KokenHealthSourced {
  meters: number;
  kilometers: number;
}

export interface KokenHealthDistanceRangePoint {
  date: string;
  meters: number;
  kilometers: number;
}

export interface KokenHealthDistanceRangeResult extends KokenHealthSourced {
  points: KokenHealthDistanceRangePoint[];
}

export interface KokenHealthEnergyToday extends KokenHealthSourced {
  kilocalories: number;
}

export interface KokenHealthExerciseToday extends KokenHealthSourced {
  minutes: number;
}

export interface KokenHealthExerciseRangePoint {
  date: string;
  minutes: number;
}

export interface KokenHealthExerciseRangeResult extends KokenHealthSourced {
  points: KokenHealthExerciseRangePoint[];
}

export interface KokenHealthSleepPhases {
  deep: number;
  rem: number;
  core: number;
  awake: number;
  inBed: number;
}

export interface KokenHealthSleepLastNight extends KokenHealthSourced {
  hours: number;
  minutes: number;
  bedtime: string | null;
  wakeTime: string | null;
  phases: KokenHealthSleepPhases;
}

export interface KokenHealthSleepNight {
  date: string;
  hours: number;
  bedtime: string;
  wakeTime: string;
}

export interface KokenHealthSleepRangeResult extends KokenHealthSourced {
  nights: KokenHealthSleepNight[];
}

export interface KokenHealthHeartRateLatest extends KokenHealthSourced {
  bpm: number | null;
  recordedAt: string | null;
}

export interface KokenHealthRestingHeartRate extends KokenHealthSourced {
  bpm: number;
}

export interface KokenHealthMindfulToday extends KokenHealthSourced {
  minutes: number;
  sessions: number;
}

export interface KokenHealthStandToday extends KokenHealthSourced {
  minutes: number;
}

export interface KokenHealthFlightsToday extends KokenHealthSourced {
  flights: number;
}

export interface HealthGoalSubscription {
  goalId: string;
  title: string;
  metric: 'steps' | 'sleep' | 'exercise' | 'distance' | 'mindful' | 'flightsClimbed' | 'water' | 'bedtime';
  threshold: number;
  unit: string;
  frequency: 'daily' | 'weekly';
  validationType: 'phoneSync' | 'smartwatch';
  targetLocalTime?: string;
}

export interface KokenHealthBackgroundStatus {
  available: boolean;
  enabled: boolean;
  notificationPermission: 'granted' | 'denied' | 'unknown';
  lastSyncAt: string | null;
}

export interface KokenHealthCapabilities {
  readBridge: boolean;
  goalSubscriptions: boolean;
  backgroundStatus: boolean;
  localThresholdNotifications: boolean;
}

// ============== KokenLocation Bridge Types (plugin GPS natif Kōken) ==============
// Le contrat du plugin GPS vit dans `kokenLocationBridge.ts`, seul point
// d'entrée des types et de la détection. Réexporté ici pour les imports
// historiques : une seule définition, donc une seule déclaration de
// `window.kokenLocation`.
export type {
  KokenBackgroundLocation,
  KokenLocationAuthorization,
  KokenLocationAuthorizationDetail,
  KokenLocationCallbackData,
  KokenLocationDiscardedDetail,
  KokenLocationErrorDetail,
  KokenLocationEventName,
  KokenLocationLastError,
  KokenLocationLastStop,
  KokenLocationPoint,
  KokenLocationSessionInfo,
  KokenLocationStoppedDetail,
} from './kokenLocationBridge';
export { isKokenLocationAvailable } from './kokenLocationBridge';

// Type declarations for Median global object
declare global {
  interface Window {
    kokenHealth?: {
      isNative: () => boolean;
      isAvailable: () => Promise<{ available: boolean }>;
      /**
       * Demande les permissions santé. Comportement plateforme :
       *
       * **iOS** : retourne `{ granted: boolean }` synchrone après le prompt
       * système HealthKit (UNUserNotificationCenter-style).
       *
       * **Android** : ouvre l'app Health Connect via le PermissionController
       * contract. La réponse est synchrone et indique seulement si le flow a
       * été LANCÉ (pas le résultat user) :
       *  - `alreadyGranted: true` → toutes les perms étaient déjà OK, pas
       *    d'UI affichée, le frontend peut commencer à lire les données
       *  - `requested: true` → flow lancé, attendre l'event JS
       *    `kokenHealth:hcPermissionsChanged` pour le résultat final
       *  - `unavailable: true` → Health Connect pas installé sur le device
       *    (proposer un deeplink Play Store ou expliquer la situation)
       *
       * Le shape `{ granted }` historique reste maintenu pour iOS via le
       * type union ci-dessous. Sur Android, `granted` n'est jamais renvoyé
       * directement — il faut consommer l'event ou re-call `getGrantedPermissions()`.
       */
      requestPermissions: (dataTypes?: string[]) => Promise<
        | { granted: boolean }
        | { alreadyGranted: boolean; requested: boolean; unavailable: boolean; error?: string }
      >;
      /**
       * Android-only — lecture passive de l'état des permissions HC.
       * N'affiche AUCUN prompt. Utiliser au boot pour décider si le permission
       * gate banner doit apparaître, et pour détecter une révocation depuis
       * l'app HC native (l'user va dans HC et désactive un toggle).
       *
       * iOS : non implémenté (HealthKit ne permet pas la lecture de l'état des
       * permissions par design Apple — seul le user le sait via l'app Réglages).
       */
      getGrantedPermissions?: () => Promise<{
        granted: string[];
        grantedCount: number;
        totalCount: number;
        allGranted: boolean;
      }>;
      // Steps
      getStepsToday: () => Promise<KokenHealthStepsToday>;
      getStepsRange: (startDate: string, endDate: string) => Promise<KokenHealthRangeResult>;
      // Distance
      getDistanceToday: () => Promise<KokenHealthDistanceToday>;
      getDistanceRange: (startDate: string, endDate: string) => Promise<KokenHealthDistanceRangeResult>;
      // Active energy
      getActiveEnergyToday: () => Promise<KokenHealthEnergyToday>;
      getActiveEnergyRange?: (startDate: string, endDate: string) => Promise<{ points: Array<{ date: string; kilocalories: number }>; source: string }>;
      // Exercise
      getExerciseToday: () => Promise<KokenHealthExerciseToday>;
      getExerciseRange: (startDate: string, endDate: string) => Promise<KokenHealthExerciseRangeResult>;
      // Sleep
      getSleepLastNight: () => Promise<KokenHealthSleepLastNight>;
      getSleepRange: (startDate: string, endDate: string) => Promise<KokenHealthSleepRangeResult>;
      // Heart rate
      getHeartRateLatest: () => Promise<KokenHealthHeartRateLatest>;
      getRestingHeartRateToday: () => Promise<KokenHealthRestingHeartRate>;
      getRestingHeartRateRange?: (startDate: string, endDate: string) => Promise<{ points: Array<{ date: string; bpm: number }>; source: string }>;
      // Mindful
      getMindfulMinutesToday: () => Promise<KokenHealthMindfulToday>;
      getMindfulMinutesRange?: (startDate: string, endDate: string) => Promise<{ points: Array<{ date: string; minutes: number }>; source: string }>;
      // Stand time
      getStandMinutesToday: () => Promise<KokenHealthStandToday>;
      // Flights climbed
      getFlightsClimbedToday: () => Promise<KokenHealthFlightsToday>;
      getFlightsClimbedRange?: (startDate: string, endDate: string) => Promise<{ points: Array<{ date: string; flights: number }>; source: string }>;
      // Background observers / subscriptions (implemented in native wrapper)
      updateGoalSubscriptions?: (subscriptions: HealthGoalSubscription[]) => Promise<{ success: boolean }>;
      clearGoalSubscriptions?: () => Promise<{ success: boolean }>;
      getHealthBackgroundStatus?: () => Promise<KokenHealthBackgroundStatus>;
      // Settings deep-link (recovery path after a denied read-permission).
      // iOS : ouvre Réglages → page de l'app (⚠️ Santé n'y figure PAS, Apple
      // l'exclut de la page par-app). Android : deeplink Health Connect
      // (fallback Settings de notre app si HC absent).
      openHealthSettings?: () => Promise<{ opened: boolean }>;
      // iOS uniquement, binaires ≥ 2026-08-14 : ouvre l'app Santé elle-même
      // (`x-apple-health://`, repli page Réglages). Vrai chemin de
      // récupération après refus : Santé > photo de profil > Apps > Kōken.
      // Son ABSENCE = ancien binaire → garder le wording « Réglages ».
      openHealthApp?: () => Promise<{
        opened: boolean;
        destination: 'health-app' | 'app-settings' | 'none';
      }>;
      // Android-only — runtime POST_NOTIFICATIONS prompt (Android 13+).
      // iOS-side cette permission est implicite via UNUserNotificationCenter,
      // côté Android elle DOIT être demandée explicitement sinon le worker
      // background ne peut pas afficher les notifs "Objectif atteint".
      // Result : `requested:true` signale que le prompt a été affiché ; le
      // résultat final s'observe via `getHealthBackgroundStatus().notificationPermission`
      // ou `window.addEventListener('kokenHealth:notificationPermissionChanged', …)`.
      requestNotificationPermission?: () => Promise<{
        granted: boolean;
        requested: boolean;
        notApplicable: boolean;
      }>;
      /**
       * Bg-sync natif → serveur (Option A). Stocke le `refresh_token` Supabase
       * (+ url/anonKey) côté natif (Keychain `kSecAttrAccessibleAfterFirstUnlock`),
       * pour que le background observer puisse, app fermée :
       *   1. rafraîchir un `access_token` via /auth/v1/token?grant_type=refresh_token
       *   2. POST les samples HealthKit à /functions/v1/persist-health-data
       * → le serveur auto-valide l'objectif (cron + realtime) sans ouvrir l'app.
       *
       * ⚠️ Le `refresh_token` ne transite QUE par le bridge in-app vers le shell
       * natif (jamais sur le réseau côté JS). Implémenté dans KokenHealthKit.swift.
       */
      updateAuth?: (args: { refreshToken: string; supabaseUrl: string; anonKey: string }) => Promise<{ success: boolean }>;
      /**
       * Efface les credentials stockés côté natif (au SIGNED_OUT). Le background
       * server-sync retombe alors sur la notification locale "à valider" seule.
       */
      clearAuth?: () => Promise<{ success: boolean }>;
      /**
       * Lit le `refresh_token` Supabase stocké côté natif (Keychain). Sert
       * UNIQUEMENT à récupérer une session vivante après un échec de refresh
       * local : le background observer iOS ROTE le refresh_token app fermée et
       * réécrit le nouveau dans le Keychain (jamais dans le `localStorage` du
       * WebView), donc au réveil le token du WebView est périmé. Résout
       * `{ refreshToken: '' }` si rien n'est stocké. Absent des builds
       * antérieurs à fix/ios-session-relogin (koken-ios-build).
       */
      getAuth?: () => Promise<{ refreshToken: string }>;
    };
    median?: {
      isNative?: boolean;
      platform?: 'ios' | 'android';
      // WebView API (cache management)
      // Documentation: https://median.co/docs/webview-cache
      webview?: {
        clearCache: () => void;
      };
      // StatusBar API
      statusbar?: {
        set: (options: { style?: 'dark' | 'light'; color?: string; overlay?: boolean }) => void;
      };
      // NavigationBar API (Android only)
      navigationbar?: {
        set: (options: { color?: string; lightIcons?: boolean }) => void;
      };
      // OneSignal Push Notifications (API v5)
      onesignal?: {
        register: () => void;
        info: () => Promise<{ 
          userId?: string;           // Legacy v11 player_id
          oneSignalId?: string;      // v5+ onesignal_id (user ID)
          pushToken?: string;        // APNs/FCM token
          subscribed?: boolean;
          hasPermission?: boolean;   // Ponts maison : autorisation système accordée
          canRequestPermission?: boolean; // Ponts maison : la boîte système n'a jamais été montrée
          externalId?: string;       // v5+ external_id we set
          subscription?: {           // v5+ subscription info (device-specific)
            id?: string;             // subscription_id - THIS IS THE DEVICE ID
            token?: string;          // push token
          };
        }>;
        // ✅ Modern API (recommended by Median)
        login: (externalId: string) => void;
        logout: () => void;
        // ✅ RGPD: Privacy consent control (for Manual Data Transmission mode)
        // @see https://median.co/docs/push-registration-and-privacy
        userPrivacyConsent?: {
          grant: () => void;
          revoke: () => void;
        };
        // ❌ Legacy API (deprecated - kept for backwards compatibility)
        setExternalUserId: (userId: string) => void;
        removeExternalUserId: () => void;
        setTags: (tags: Record<string, string>) => void;
        sendTag: (key: string, value: string) => void;
        deleteTag: (key: string) => void;
      };
      // Share API
      share?: {
        native: (options: { text?: string; url?: string; title?: string }) => void;
      };
      // Haptics API (Official Median.co API)
      // Documentation: https://docs.median.co/docs/haptics
      haptics?: {
        trigger: (options: { style: MedianHapticStyle }) => void;
      };
      // Health Bridge API
      healthBridge?: {
        requestPermissions: (dataTypes: HealthDataType[]) => Promise<HealthPermissionsResult>;
        getData: (options: {
          dataTypes: HealthDataType[];
          startDate: string;
          endDate: string;
          bucket?: 'raw' | 'minute' | 'hour' | 'day';
        }) => Promise<HealthBridgeResponse>;
      };
      // Biometric Auth API (FaceID / TouchID / Android Biometric)
      // Documentation: https://docs.median.co/docs/faceid-touchid-android-biometric
      auth?: {
        status: () => Promise<{
          hasTouchId: boolean;
          hasSecret: boolean;
          biometryType?: 'faceId' | 'touchId' | 'none';
        }>;
        save: (options: { secret: string; minimumAndroidBiometric?: 'strong' | 'weak' }) => Promise<{ success: boolean; error?: string }>;
        get: (options?: { prompt?: string; callbackOnCancel?: number; minimumAndroidBiometric?: 'strong' | 'weak' }) => Promise<{
          success: boolean;
          secret?: string;
          error?: string;
        }>;
        delete: () => Promise<{ success: boolean }>;
      };
      // Native Datastore (App Storage)
      // Documentation: https://docs.median.co/docs/native-datastore
      storage?: {
        app: {
          get: (options: { key: string }) => Promise<{ data?: string }>;
          set: (options: { key: string; value: string }) => Promise<void>;
          delete: (options: { key: string }) => Promise<void>;
        };
      };
      // App Review API
      // Documentation: https://docs.median.co/docs/app-review
      appreview?: {
        prompt: () => void;
      };
      // Social Login API
      // Documentation: https://docs.median.co/docs/social-login
      socialLogin?: {
        google: {
          login: () => Promise<{
            success: boolean;
            email?: string;
            name?: string;
            idToken?: string;
            accessToken?: string;
            error?: string;
          }>;
          logout: () => Promise<void>;
        };
        apple: {
          login: () => Promise<{
            success: boolean;
            email?: string;
            name?: string;
            idToken?: string;
            userId?: string;
            error?: string;
          }>;
        };
        facebook: {
          login: () => Promise<{
            success: boolean;
            email?: string;
            name?: string;
            accessToken?: string;
            userId?: string;
            error?: string;
          }>;
          logout: () => Promise<void>;
        };
      };
    };
  }
}

// ============== Detection ==============

/**
 * Coque iOS maison (Swift, depuis le 03/10/2026), reconnue à son gestionnaire
 * de messages `kokenCore`.
 *
 * Ce gestionnaire existe depuis la première version de la coque (build 71).
 * L'agent utilisateur ne porte « KokenApp/ » que depuis le build 77 : il ne
 * suffit donc pas. Safari n'expose pas `window.webkit.messageHandlers`, et la
 * coque Median a `JSBridge` mais pas `kokenCore` : seul un hôte qui enregistre
 * ce gestionnaire le crée.
 *
 * Même test que `isKokenIosShell` de la PR #1501 (platformDetection), dupliqué
 * ici pour ne dépendre ni de cette PR ni de platformDetection (cycle d'import).
 */
const isKokenIosShell = (): boolean => {
  if (typeof window === 'undefined') return false;
  const handlers = (window as unknown as {
    webkit?: { messageHandlers?: Record<string, unknown> };
  }).webkit?.messageHandlers;
  return !!handlers?.kokenCore && !handlers?.JSBridge;
};

/**
 * Check if running in Median native wrapper
 */
export const isMedianNative = (): boolean => {
  if (typeof window === 'undefined') return false;

  // ⚠️ La coque ANDROID doit être testée EN PREMIER, avant `median-js-bridge`.
  //
  // Le SDK implémente `isNativeApp()` ainsi :
  //     !!window?.webkit?.messageHandlers?.JSBridge || !!window?.JSBridge
  // Notre coque Android est la nôtre, elle n'injecte aucun `JSBridge`. Le SDK
  // renvoie donc `false` — SANS lever d'exception, donc le `catch` ci-dessous
  // n'était JAMAIS atteint et le repli ne servait à rien.
  //
  // Mesuré au CDP sur émulateur le 04/08/2026 :
  //     window.JSBridge = "undefined", webkit.JSBridge = false,
  //     UA contient "KokenAndroid" = true
  // Conséquence : cette fonction renvoyait `false` dans l'app Android, et TOUT
  // ce qu'elle garde était mort en silence — enregistrement au push, pose de
  // l'external_id, handler de tap sur notification, biométrie, thème des
  // barres système. C'est la cause racine unique de ces pannes, en amont même
  // du pont OneSignal.
  //
  // Même test que `platformDetection.isAndroidApp()`, dupliqué ici plutôt
  // qu'importé pour ne pas créer de cycle entre les deux modules.
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  if (ua.includes('KokenAndroid') || (window as { isGoNative?: boolean }).isGoNative === true) {
    return true;
  }

  // La coque iOS maison n'a ni `JSBridge` ni `isGoNative` : sans ce test la
  // fonction rendait `false` sur iPhone, et tout ce qu'elle garde y était éteint
  // (connexion OneSignal, jeton, toucher d'une notification, retour haptique).
  if (isKokenIosShell()) return true;

  try {
    return Median.isNativeApp();
  } catch {
    // Fallback: detect presence of Median injected APIs
    return (
      !!window.median &&
      (window.median.isNative === true ||
        typeof window.median.platform !== 'undefined' ||
        typeof window.median.haptics !== 'undefined' ||
        typeof window.median.onesignal !== 'undefined' ||
        typeof window.median.webview !== 'undefined')
    );
  }
};

/**
 * Get current platform
 */
export const getMedianPlatform = (): 'ios' | 'android' | 'web' => {
  if (!isMedianNative()) return 'web';

  // Coque iOS maison : la réponse est connue sans lire l'agent utilisateur.
  // Sur iPad, celui d'une vue web peut être celui d'un Mac : la fonction
  // rendrait 'web' et pushTokenService prendrait le rythme d'essais d'Android.
  if (isKokenIosShell()) return 'ios';

  const p = window.median?.platform;
  if (p === 'ios' || p === 'android') return p;

  // Last resort: infer from UA inside native webview
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'web';
};

/**
 * Check if iOS
 */
export const isMedianIOS = (): boolean => getMedianPlatform() === 'ios';

/**
 * Check if Android
 */
export const isMedianAndroid = (): boolean => getMedianPlatform() === 'android';

/**
 * Check if the custom KokenHealthKit bridge is available (iOS native, injected
 * by KokenHealthKit.swift via window.kokenHealth). Independent from Median's
 * paid HealthBridge — if this is true we always prefer it.
 */
export const isKokenHealthAvailable = (): boolean => {
  if (typeof window === 'undefined') return false;
  return !!window.kokenHealth && typeof window.kokenHealth.getStepsToday === 'function';
};

/**
 * Indique si le canal natif Android Santé Connect est présent
 * (`window.kokenHealthAndroid`, injecté par le module Kotlin du dépôt de la
 * coque `koken-ios-build`, dossier android/app/src/main/java/com/koken/healthkit/).
 *
 * État au 17/10/2026 : le module existe (9 permissions de lecture Santé Connect,
 * androidx.health.connect:connect-client 1.1.0-alpha07). Il n'est pas encore
 * publié sur le Play Store et aucune première synchronisation réelle n'a été
 * constatée.
 *
 * Attention : `window.kokenHealthAndroid` n'est que le canal `postMessage`.
 * L'API santé elle-même est exposée par le shim KokenHealthKitBridge.js sous
 * `window.kokenHealth`, identique à iOS (sources `'wear_os' | 'health_connect' |
 * 'health_connect:<package>'`) : sur Android, `isKokenHealthAvailable()` est
 * donc vrai lui aussi et `getActiveHealthBridge()` répond `'kokenhealth_ios'`.
 */
export const isKokenHealthAndroidAvailable = (): boolean => {
  if (typeof window === 'undefined') return false;
  const win = window as any;
  return !!win.kokenHealthAndroid;
};

export const getKokenHealthCapabilities = (): KokenHealthCapabilities => {
  if (!isKokenHealthAvailable() || !window.kokenHealth) {
    return {
      readBridge: false,
      goalSubscriptions: false,
      backgroundStatus: false,
      localThresholdNotifications: false,
    };
  }

  const goalSubscriptions =
    typeof window.kokenHealth.updateGoalSubscriptions === 'function' &&
    typeof window.kokenHealth.clearGoalSubscriptions === 'function';
  const backgroundStatus = typeof window.kokenHealth.getHealthBackgroundStatus === 'function';

  return {
    readBridge: true,
    goalSubscriptions,
    backgroundStatus,
    // The webview can only rely on threshold-triggered native notifications
    // once it can publish goal subscriptions and read back native status.
    localThresholdNotifications: goalSubscriptions && backgroundStatus,
  };
};

/**
 * Check if any health bridge is available.
 *
 * Koken ne souscrit PAS au plugin payant Median HealthBridge. La stratégie
 * santé est 100 % custom :
 *   1. `window.kokenHealth` — plugin iOS natif `KokenHealthKit.swift` (actif)
 *   2. `window.kokenHealthAndroid` : canal natif du module Kotlin Santé Connect
 *      (coque `koken-ios-build`, dossier android/). Sur Android, le shim de ce
 *      module expose aussi `window.kokenHealth`, donc le cas 1 répond déjà vrai.
 *      Pas encore publié sur le Play Store ni vu en synchronisation réelle.
 *
 * Le `window.median?.healthBridge` n'est JAMAIS présent en prod (plan non
 * souscrit). La vérif ci-dessous est conservée comme safety net si le plan
 * est activé un jour, mais retourne faux en pratique.
 */
export const isHealthBridgeAvailable = (): boolean => {
  if (isKokenHealthAvailable()) return true;
  if (isKokenHealthAndroidAvailable()) return true;
  // Safety net dormant : `window.median.healthBridge` nécessite souscription
  // payante côté Median, que Koken n'active pas. Retourne false en prod.
  return isMedianNative() && !!window.median?.healthBridge;
};

/**
 * Identifie la plateforme santé active (pour logs / analytics / source-tag).
 * Retourne `null` si aucun bridge custom Koken n'est disponible (PWA web ou
 * device sans plugin installé → fallback GPS uniquement).
 */
export const getActiveHealthBridge = ():
  | 'kokenhealth_ios'
  | 'kokenhealth_android'
  | null => {
  if (isKokenHealthAvailable()) return 'kokenhealth_ios';
  if (isKokenHealthAndroidAvailable()) return 'kokenhealth_android';
  return null;
};

// ============== StatusBar ==============

export interface StatusBarOptions {
  style?: 'dark' | 'light';
  color?: string;
  overlay?: boolean;
}

/**
 * Set status bar style and color
 */
export const setStatusBar = (options: StatusBarOptions): void => {
  if (!isMedianNative() || !window.median?.statusbar) return;

  try {
    window.median.statusbar.set(options);
  } catch (error) {
    console.warn('[MedianBridge] StatusBar error:', error);
  }
};

/**
 * Set dark status bar (dark background + light icons).
 * Median's `style` is the theme preset name, not the icon color — `'dark'`
 * yields a dark bar with light icons.
 */
export const setDarkStatusBar = (): void => {
  setStatusBar({ style: 'dark' });
};

export const setLightStatusBar = (): void => {
  setStatusBar({ style: 'light' });
};

// ============== NavigationBar (Android) ==============

export interface NavigationBarOptions {
  color?: string;
  lightIcons?: boolean;
}

/**
 * Set Android navigation bar style
 */
export const setNavigationBar = (options: NavigationBarOptions): void => {
  if (!isMedianAndroid() || !window.median?.navigationbar) return;

  try {
    window.median.navigationbar.set(options);
  } catch (error) {
    console.warn('[MedianBridge] NavigationBar error:', error);
  }
};

// ============== Haptics (Official Median API) ==============

/**
 * Median Haptic Styles (Official API)
 * @see https://docs.median.co/docs/haptics
 */
export type MedianHapticStyle =
  | 'impactLight'
  | 'impactMedium'
  | 'impactHeavy'
  | 'notificationSuccess'
  | 'notificationWarning'
  | 'notificationError'
  | 'tick'        // Android only
  | 'click'       // Android only
  | 'double_click'; // Android only

/**
 * Trigger haptic feedback via official Median API
 * @see https://docs.median.co/docs/haptics
 */
export const triggerMedianHaptic = (
  type: 'impact-light' | 'impact-medium' | 'impact-heavy' | 'success' | 'warning' | 'error' | 'selection'
): void => {
  if (!isMedianNative()) return;
  
  if (!window.median?.haptics) {
    console.warn('[MedianBridge] No haptics API found (window.median.haptics)');
    return;
  }

  // Map internal types to official Median styles
  const styleMap: Record<string, MedianHapticStyle> = {
    'selection': 'impactLight',
    'impact-light': 'impactLight',
    'impact-medium': 'impactMedium',
    'impact-heavy': 'impactHeavy',
    'success': 'notificationSuccess',
    'warning': 'notificationWarning',
    'error': 'notificationError'
  };

  try {
    window.median.haptics.trigger({ style: styleMap[type] });
    console.log('[MedianBridge] Haptic triggered successfully:', type);
  } catch (error) {
    console.warn('[MedianBridge] Haptic error:', error);
  }
};

// ============== OneSignal Push Notifications ==============

/**
 * Les ponts maison (iOS et Android) rendent une promesse, rejetée au bout de
 * 10 s sans réponse du natif. Les fonctions ci-dessous n'attendent pas ce
 * retour : un rejet que personne ne lit partirait en rapport
 * `unhandled_rejection`. Le pont Median ne rend rien : rien à faire alors.
 */
export const ignoreBridgeRejection = (result: unknown): void => {
  const maybePromise = result as { catch?: (onRejected: () => void) => unknown } | null | undefined;
  if (maybePromise && typeof maybePromise.catch === 'function') {
    maybePromise.catch(() => {});
  }
};

const IOS_PUSH_PERMISSION_ASKED_KEY = 'koken_ios_push_permission_asked';

/**
 * Demande d'autorisation automatique sur la coque iOS maison : une seule fois
 * par installation, et seulement si le pont dit que l'autorisation manque.
 *
 * Jusqu'au build 77 la coque demande l'autorisation elle-même au lancement et
 * répond `{}` à info() : on n'appelle rien. Les builds suivants ne demandent
 * plus rien seuls et comptent sur cet appel. Rappeler register() à chaque
 * événement de session rouvrirait, pour qui a refusé, la boîte « Open
 * Settings » de OneSignal. Les boutons explicites des réglages appellent le
 * pont en direct et ne passent pas par ici.
 */
const requestIosPushPermissionOnce = async (): Promise<void> => {
  try {
    const onesignal = window.median?.onesignal;
    if (!onesignal) return;

    const info = await onesignal.info();
    if (info?.hasPermission !== false) return;
    // iOS sait si la boîte système a déjà été montrée. Quand le pont le dit,
    // on le croit : la clé ci-dessous disparaît avec les données du site.
    if (info.canRequestPermission === false) return;

    if (localStorage.getItem(IOS_PUSH_PERMISSION_ASKED_KEY)) return;
    localStorage.setItem(IOS_PUSH_PERMISSION_ASKED_KEY, new Date().toISOString());

    ignoreBridgeRejection(onesignal.register());
  } catch {
    // Pont muet, délai dépassé ou stockage indisponible : on ne demande rien.
  }
};

/**
 * Register for push notifications
 */
export const registerPushNotifications = (): void => {
  if (!isMedianNative() || !window.median?.onesignal) return;

  if (isKokenIosShell()) {
    void requestIosPushPermissionOnce();
    return;
  }

  try {
    ignoreBridgeRejection(window.median.onesignal.register());
  } catch (error) {
    console.warn('[MedianBridge] OneSignal register error:', error);
  }
};

/**
 * ✅ RGPD: Accorde le consentement privacy à OneSignal via Median Bridge
 * Doit être appelé après que l'utilisateur a donné son consentement data_processing
 * Requis si "Automatic Data Transmission" est en mode Manual dans Median
 * @see https://median.co/docs/push-registration-and-privacy
 */
export const grantOneSignalPrivacyConsent = (): void => {
  if (!isMedianNative() || !window.median?.onesignal) return;
  try {
    ignoreBridgeRejection(window.median.onesignal.userPrivacyConsent?.grant?.());
    console.log('[MedianBridge] ✅ OneSignal privacy consent granted');
  } catch (error) {
    console.warn('[MedianBridge] userPrivacyConsent.grant error:', error);
  }
};

/**
 * ✅ RGPD: Révoque le consentement privacy à OneSignal via Median Bridge
 * À appeler si l'utilisateur retire son consentement data_processing
 * @see https://median.co/docs/push-registration-and-privacy
 */
export const revokeOneSignalPrivacyConsent = (): void => {
  if (!isMedianNative() || !window.median?.onesignal) return;
  try {
    ignoreBridgeRejection(window.median.onesignal.userPrivacyConsent?.revoke?.());
    console.log('[MedianBridge] ✅ OneSignal privacy consent revoked');
  } catch (error) {
    console.warn('[MedianBridge] userPrivacyConsent.revoke error:', error);
  }
};

/**
 * Get OneSignal info (v5+ compatible)
 * Returns both onesignal_id (user) and subscription_id (device)
 */
export const getOneSignalInfo = async (): Promise<{
  userId?: string;           // onesignal_id (OneSignal User ID)
  subscriptionId?: string;   // subscription_id (device-specific)
  pushToken?: string;
  subscribed?: boolean;
  externalId?: string;       // external_id we set (Supabase user ID)
} | null> => {
  if (!isMedianNative() || !window.median?.onesignal) return null;

  try {
    const info = await window.median.onesignal.info();
    console.log('[MedianBridge] Raw OneSignal info:', JSON.stringify(info));
    
    return {
      // Use oneSignalId (v5) or userId (legacy) for the user ID
      userId: info.oneSignalId || info.userId,
      // subscription.id is the device-specific ID we need for push targeting
      subscriptionId: info.subscription?.id,
      // Push token from subscription or legacy field
      pushToken: info.subscription?.token || info.pushToken,
      subscribed: info.subscribed,
      externalId: info.externalId
    };
  } catch (error) {
    console.warn('[MedianBridge] OneSignal info error:', error);
    return null;
  }
};

/**
 * Dit si le pont OneSignal de la coque iOS maison est muet.
 *
 * Les builds 71 à 77 répondent `{}` à info() (le Swift renvoie du texte, que
 * le script du pont ne sait pas lire) : ils ne donneront jamais d'identifiant,
 * inutile de les attendre. Les builds suivants répondent toujours avec des
 * clés. Android et la coque Median ne sont pas concernés : `false`.
 */
export const isOneSignalBridgeMute = async (): Promise<boolean> => {
  if (!isKokenIosShell() || !window.median?.onesignal) return false;

  try {
    const info = await window.median.onesignal.info();
    return !!info && typeof info === 'object' && Object.keys(info).length === 0;
  } catch {
    return false;
  }
};

/**
 * ✅ MODERN API: Login user to OneSignal (recommended by Median)
 * Associates the device subscription with the user's external ID
 * Must be called after successful authentication
 */
export const loginOneSignal = (externalId: string): void => {
  if (!isMedianNative() || !window.median?.onesignal) return;

  try {
    ignoreBridgeRejection(window.median.onesignal.login(externalId));
    console.log('[MedianBridge] ✅ OneSignal login called with:', externalId);
  } catch (error) {
    console.warn('[MedianBridge] OneSignal login error:', error);
  }
};

/**
 * ✅ MODERN API: Logout user from OneSignal (recommended by Median)
 * Releases the device subscription association
 * Must be called BEFORE supabase.auth.signOut()
 */
export const logoutOneSignal = (): void => {
  if (!isMedianNative() || !window.median?.onesignal) return;

  try {
    ignoreBridgeRejection(window.median.onesignal.logout());
    console.log('[MedianBridge] ✅ OneSignal logout called');
  } catch (error) {
    console.warn('[MedianBridge] OneSignal logout error:', error);
  }
};

/**
 * @deprecated Use loginOneSignal() instead - legacy API for backwards compatibility
 * Set external user ID for targeting
 */
export const setOneSignalExternalUserId = (userId: string): void => {
  if (!isMedianNative() || !window.median?.onesignal) return;

  try {
    // ✅ Use modern login() instead of legacy setExternalUserId()
    ignoreBridgeRejection(window.median.onesignal.login(userId));
    console.log('[MedianBridge] OneSignal login (via setExternalUserId wrapper):', userId);
  } catch (error) {
    console.warn('[MedianBridge] OneSignal setExternalUserId error:', error);
  }
};

/**
 * Set tags for segmentation
 */
export const setOneSignalTags = (tags: Record<string, string>): void => {
  if (!isMedianNative() || !window.median?.onesignal) return;

  try {
    ignoreBridgeRejection(window.median.onesignal.setTags(tags));
  } catch (error) {
    console.warn('[MedianBridge] OneSignal setTags error:', error);
  }
};

// ============== Push Notification Click Handler ==============

/**
 * Deep-link au tap d'une notification push.
 *
 * ⚠️ Histoire du bug (2026-08-03) : l'ancienne implémentation s'abonnait à
 * `Median.onesignal.notificationOpened`, une API qui N'EXISTE PAS dans
 * median-js-bridge (le TypeError était avalé par un try/catch) — le handler
 * n'a donc JAMAIS été enregistré en prod. La vraie API est
 * `Median.oneSignalPushOpened.addListener(cb)` et `cb` reçoit directement
 * l'additionalData du push (`{ action_url, notification_type, ... }`).
 * Ne jamais réintroduire de cast `as any` ici : c'est lui qui a masqué le bug.
 */
let pushClickHandlerInitialized = false;

/**
 * Navigation SPA sans reload : react-router (BrowserRouter) écoute popstate.
 * Le fallback location.href couvre un état où l'history serait indisponible.
 */
const navigateToActionUrl = (actionUrl: string): void => {
  try {
    window.history.pushState(null, '', actionUrl);
    window.dispatchEvent(new PopStateEvent('popstate'));
  } catch {
    // Seconde barrière : le repli ne charge jamais une autre origine. Un
    // pushState refusé est justement le signe d'une adresse qui sort du site.
    try {
      if (new URL(actionUrl, window.location.origin).origin !== window.location.origin) return;
    } catch {
      return;
    }
    window.location.href = actionUrl;
  }
};

/**
 * Télémétrie de clic, fire-and-forget : sans elle, un tap perdu est invisible
 * (aucun appelant de track-push-notification-click avant 2026-08-03).
 * Nécessite une session (RLS côté edge = service role, mais on veut un userId
 * fiable) : au cold start elle part après l'auth, sinon immédiatement.
 */
const trackPushOpen = (data: Record<string, unknown>, actionUrl: string): void => {
  void (async () => {
    try {
      const { supabase } = await import('@/integrations/supabase/client');
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;
      if (!userId) return;

      const batchId = typeof data.push_batch_id === 'string' ? data.push_batch_id : undefined;
      const notificationLogId =
        typeof data.notification_log_id === 'string' ? data.notification_log_id : undefined;
      if (!batchId && !notificationLogId) return;

      await supabase.functions.invoke('track-push-notification-click', {
        body: {
          batchId,
          notificationLogId,
          userId,
          deepLink: actionUrl,
          deviceInfo: { platform: 'median-native' },
        },
      });
    } catch {
      // Télémétrie best-effort : ne jamais gêner la navigation.
    }
  })();
};

// Dédoublonnage : le natif appelle jusqu'à 3 callbacks globaux pour la MÊME
// livraison (median_…, gonative_…, puis _median_…) et l'ordre n'est pas
// contractuel selon les versions du shell.
let lastHandledPushKey: string | null = null;
let lastHandledPushAt = 0;

const handlePushOpenPayload = (data: Record<string, unknown> | null | undefined): void => {
  // Le natif appelle TOUS les callbacks globaux (alias buffer inclus) : on
  // efface le buffer pour qu'un doublon ne soit pas rejoué au prochain boot.
  clearPendingPush();

  const actionUrl = extractInternalActionUrl(data);
  if (!actionUrl) {
    console.log('[MedianBridge] ℹ️ Push sans action_url interne, ignoré');
    return;
  }

  let key = actionUrl;
  try {
    key = JSON.stringify(data);
  } catch {
    /* payload cyclique improbable : l'actionUrl suffit comme clé */
  }
  if (key === lastHandledPushKey && Date.now() - lastHandledPushAt < 5000) {
    return;
  }
  lastHandledPushKey = key;
  lastHandledPushAt = Date.now();

  console.log('[MedianBridge] 🔗 Push tap → navigation vers', actionUrl);
  trackPushOpen(data as Record<string, unknown>, actionUrl);
  navigateToActionUrl(actionUrl);
};

export const initPushClickHandler = (): void => {
  if (pushClickHandlerInitialized) return;
  if (!isMedianNative()) return;

  try {
    // Pas de Median.onReady ici : addListener ne fait que définir le callback
    // global + poster l'abonnement au webkit message handler, disponibles dès
    // la création de la WebView. Attendre onReady (tick 500 ms) rouvrirait la
    // fenêtre de perte au cold start.
    Median.oneSignalPushOpened.addListener((data) => {
      handlePushOpenPayload(data as Record<string, unknown>);
    });

    // median-js-bridge ne reprend QUE _median_onesignal_push_opened : sans les
    // lignes ci-dessous, les deux alias historiques resteraient le buffer
    // d'index.html à vie et réécriraient en sessionStorage un payload déjà
    // traité (rejeu possible à un reload < TTL). On les pointe sur le même
    // handler (dédoublonné), ce qui couvre aussi un shell qui n'appellerait
    // que les alias.
    const w = window as unknown as Record<string, unknown>;
    w.median_onesignal_push_opened = (d: unknown) =>
      handlePushOpenPayload(d as Record<string, unknown>);
    w.gonative_onesignal_push_opened = (d: unknown) =>
      handlePushOpenPayload(d as Record<string, unknown>);

    pushClickHandlerInitialized = true;

    // Rattrapage : payload délivré avant l'enregistrement du listener.
    const pending = readAndClearPendingPush();
    if (pending) {
      handlePushOpenPayload(pending);
    } else if (window.__kokenBootPushForTracking) {
      // Le chemin boot (main.tsx) a déjà navigué ; il ne reste que la télémétrie.
      const bootData = window.__kokenBootPushForTracking;
      delete window.__kokenBootPushForTracking;
      const bootUrl = extractInternalActionUrl(bootData);
      if (bootUrl) trackPushOpen(bootData, bootUrl);
    }

    console.log('[MedianBridge] ✅ Push click handler initialized');
  } catch (error) {
    console.warn('[MedianBridge] Push click handler setup error:', error);
    // Ce chemin d'échec est resté invisible pendant des mois (console.warn
    // seul) : on le remonte désormais au pipeline de monitoring.
    void import('@/services/errorReportQueue').then(({ queueErrorReport }) =>
      queueErrorReport({
        errorType: 'push_click_handler_init_failed',
        errorMessage: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        route: window.location.pathname,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
      }),
    ).catch(() => {});
  }
};

// ============== Share ==============

export interface ShareOptions {
  text?: string;
  url?: string;
  title?: string;
}

/**
 * Trigger native share dialog using official Median SDK
 * Falls back to Web Share API, then clipboard
 * @deprecated Préférer UnifiedShareService.shareSimple pour les nouveaux développements
 */
export const shareNative = async (options: ShareOptions): Promise<boolean> => {
  // Import dynamique pour éviter les dépendances circulaires
  const { UnifiedShareService } = await import('@/services/UnifiedShareService');
  return UnifiedShareService.shareSimple(options);
};

// ============== Health Bridge ==============

/**
 * Request health data permissions via le plugin custom Koken.
 *
 * Routing :
 *   - iOS : `window.kokenHealth.requestPermissions()` (KokenHealthKit.swift)
 *   - Android : module Kotlin Santé Connect, appelé lui aussi via
 *     `window.kokenHealth.requestPermissions()` (shim JS de la coque, canal natif
 *     `window.kokenHealthAndroid`) ; pas encore publié sur le Play Store.
 *
 * Supporte 9 types : steps, distance, activeEnergy, exerciseTime, sleep,
 * heartRate, mindful, stand, flightsClimbed.
 *
 * Les types legacy (height, weight, bmi, calorieIntake, waterIntake) sont
 * silencieusement ignorés — ils nécessitaient le plugin payant Median
 * HealthBridge, non souscrit par Koken.
 */

export const callAndroidHealthBridge = async (method: string, params: Record<string, any> = {}): Promise<any> => {
  return new Promise((resolve) => {
    const callbackId = `health_${method}_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
    const win = window as any;
    win[callbackId] = (result: any) => {
      delete win[callbackId];
      let parsedResult = result;
      if (typeof result === 'string') {
        try {
          parsedResult = JSON.parse(result);
        } catch (e) {
          console.warn(`[KokenHealthAndroid] Failed to parse result for ${method}`, e);
        }
      }
      resolve(parsedResult);
    };

    try {
      win.kokenHealthAndroid.postMessage(JSON.stringify({
        method,
        callbackId,
        params
      }));
    } catch (e) {
      console.warn(`[KokenHealthAndroid] Failed to postMessage for ${method}`, e);
      delete win[callbackId];
      resolve(null);
    }
  });
};
export const requestHealthPermissions = async (
  dataTypes: HealthDataType[]
): Promise<HealthPermissionsResult | null> => {
  // Prefer the custom KokenHealthKit bridge
  if (isKokenHealthAvailable() && window.kokenHealth) {
    try {
      // KokenHealthKit natively supports these data types.
      const supportedByKokenHealth = new Set<HealthDataType>([
        'steps', 'distance', 'activeEnergy', 'exerciseTime', 'sleep',
        'heartRate', 'mindful', 'stand', 'flightsClimbed',
      ]);
      const requested = dataTypes.filter(t => supportedByKokenHealth.has(t));
      const unsupported = dataTypes.filter(t => !supportedByKokenHealth.has(t));

      const result = await window.kokenHealth.requestPermissions(requested);
      console.log('[KokenHealth] permissions result:', result);

      // KokenHealthKit returns a single boolean for the whole batch — we
      // mirror that into the granted/declined arrays the React layer expects.
      const isGranted = 'granted' in result
        ? result.granted
        : (result.alreadyGranted || result.requested);
      return {
        granted: isGranted ? requested : [],
        declined: isGranted ? unsupported : dataTypes
      };
    } catch (error) {
      console.warn('[KokenHealth] permissions error:', error);
      return null;
    }
  }

  if (isKokenHealthAndroidAvailable() && (window as any).kokenHealthAndroid) {
    const parsedResult = await callAndroidHealthBridge('requestPermissions', { dataTypes });
    const isGranted = parsedResult && (parsedResult.granted === true || parsedResult.alreadyGranted || parsedResult.requested);
    return {
      granted: isGranted ? dataTypes : [],
      declined: isGranted ? [] : dataTypes
    };
  }

  // Safety net dormant vers le plugin payant Median HealthBridge.
  // ⚠️ Koken ne souscrit PAS à ce plugin — le chemin ci-dessous retourne
  // toujours `null` en prod. Conservé uniquement pour ne pas casser un
  // éventuel test ou si le plan payant est un jour activé.
  if (!isHealthBridgeAvailable() || !window.median?.healthBridge) {
    console.warn('[KokenHealth] No native health plugin available (iOS Koken missing or Android plugin not deployed yet)');
    return null;
  }

  try {
    const result = await window.median.healthBridge.requestPermissions(dataTypes);
    console.log('[MedianBridge-dormant] Health permissions result:', result);
    return result;
  } catch (error) {
    console.warn('[MedianBridge-dormant] Health permissions error:', error);
    return null;
  }
};

/**
 * Get health data for specified types and date range.
 *
 * ⚠️ Cette fonction utilise l'API `window.median.healthBridge.getData()` du
 * plugin **payant Median** que Koken ne souscrit pas. Elle retourne donc
 * toujours `null` en prod. Les appelants modernes doivent utiliser
 * directement les helpers `window.kokenHealth.get*` (via `getTodaySteps`,
 * `getWeeklySteps`, etc. plus bas dans ce fichier).
 *
 * Conservé pour compatibilité avec d'éventuels consumers historiques.
 */
export const getHealthData = async (options: {
  dataTypes: HealthDataType[];
  startDate: string;
  endDate: string;
  bucket?: 'raw' | 'minute' | 'hour' | 'day';
}): Promise<HealthBridgeResponse | null> => {
  if (!isHealthBridgeAvailable() || !window.median?.healthBridge) {
    console.warn('[KokenHealth] getHealthData called but Median HealthBridge (paid) not available. Use window.kokenHealth.get* helpers instead.');
    return null;
  }

  try {
    const result = await window.median.healthBridge.getData(options);
    return result;
  } catch (error) {
    console.warn('[MedianBridge-dormant] Health data error:', error);
    return null;
  }
};

/**
 * Get today's total steps.
 * Routes vers `window.kokenHealth.getStepsToday()` (plugin iOS natif).
 * Le chemin `window.median.healthBridge` est un fallback dormant (plan
 * payant non souscrit chez Koken) — retourne `null` en prod.
 */
export const getTodaySteps = async (): Promise<number | null> => {
  if (isKokenHealthAvailable() && window.kokenHealth) {
    try {
      const result = await window.kokenHealth.getStepsToday();
      return result.steps ?? 0;
    } catch (error) {
      console.warn('[KokenHealth] getStepsToday error:', error);
      return null;
    }
  }

  if (isKokenHealthAndroidAvailable()) {
    try {
      const result = await callAndroidHealthBridge('getStepsToday');
      return result?.steps ?? 0;
    } catch (error) {
      console.warn('[KokenHealthAndroid] getStepsToday error:', error);
      return null;
    }
  }

  const today = new Date();
  const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  const result = await getHealthData({
    dataTypes: ['steps'],
    startDate: startOfDay.toISOString(),
    endDate: today.toISOString(),
    bucket: 'day'
  });

  if (!result?.data?.steps?.length) return null;

  // Sum all steps data points for today
  return result.data.steps.reduce((sum, point) => sum + point.value, 0);
};

/**
 * Get weekly steps data (last 7 days).
 * Routes vers `window.kokenHealth.getStepsRange()` (plugin iOS natif,
 * bucketed par jour). Fallback Median HealthBridge dormant (non souscrit).
 */
export const getWeeklySteps = async (): Promise<HealthBridgeDataPoint[] | null> => {
  const today = new Date();
  const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

  if (isKokenHealthAvailable() && window.kokenHealth) {
    try {
      const result = await window.kokenHealth.getStepsRange(
        weekAgo.toISOString(),
        today.toISOString()
      );
      // Adapt KokenHealth's {date, steps} to Median's {start, end, value}
      return result.points.map(p => {
        const start = new Date(p.date);
        const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);
        return {
          start: start.toISOString(),
          end: end.toISOString(),
          value: p.steps
        };
      });
    } catch (error) {
      console.warn('[KokenHealth] getStepsRange error:', error);
      return null;
    }
  }

  if (isKokenHealthAndroidAvailable()) {
    try {
      const result = await callAndroidHealthBridge('getStepsRange', {
        startDate: weekAgo.toISOString(),
        endDate: today.toISOString()
      });
      return (result?.points || []).map((p: any) => {
        const start = new Date(p.date);
        const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);
        return {
          start: start.toISOString(),
          end: end.toISOString(),
          value: p.steps
        };
      });
    } catch (error) {
      console.warn('[KokenHealthAndroid] getStepsRange error:', error);
      return null;
    }
  }

  const result = await getHealthData({
    dataTypes: ['steps'],
    startDate: weekAgo.toISOString(),
    endDate: today.toISOString(),
    bucket: 'day'
  });

  return result?.data?.steps || null;
};

/**
 * Get sleep data for the last N days.
 * Routes vers `window.kokenHealth.getSleepRange()` (plugin iOS, natif
 * HKCategoryType.sleepAnalysis). Fallback Median HealthBridge dormant
 * (plan payant non souscrit chez Koken).
 *
 * Each data point represents one night of sleep in MINUTES (to match
 * the legacy shape downstream consumers expect).
 */
export const getSleepData = async (days: number = 7): Promise<HealthBridgeDataPoint[] | null> => {
  const today = new Date();
  const startDate = new Date(today.getTime() - days * 24 * 60 * 60 * 1000);

  if (isKokenHealthAvailable() && window.kokenHealth) {
    try {
      const result = await window.kokenHealth.getSleepRange(
        startDate.toISOString(),
        today.toISOString()
      );
      return result.nights.map(n => {
        const start = new Date(n.bedtime);
        const end = new Date(n.wakeTime);
        return {
          start: start.toISOString(),
          end: end.toISOString(),
          value: n.hours * 60, // legacy: minutes
        };
      });
    } catch (error) {
      console.warn('[KokenHealth] getSleepRange error:', error);
      return null;
    }
  }

  if (isKokenHealthAndroidAvailable()) {
    try {
      const result = await callAndroidHealthBridge('getSleepRange', {
        startDate: startDate.toISOString(),
        endDate: today.toISOString()
      });
      return (result?.nights || []).map((n: any) => {
        const start = new Date(n.bedtime);
        const end = new Date(n.wakeTime);
        return {
          start: start.toISOString(),
          end: end.toISOString(),
          value: n.hours * 60, // legacy: minutes
        };
      });
    } catch (error) {
      console.warn('[KokenHealthAndroid] getSleepRange error:', error);
      return null;
    }
  }

  const result = await getHealthData({
    dataTypes: ['sleep'],
    startDate: startDate.toISOString(),
    endDate: today.toISOString(),
    bucket: 'day'
  });

  return result?.data?.sleep || null;
};

/**
 * Get last night's sleep duration in hours. Routes to KokenHealthKit's
 * dedicated getSleepLastNight (which returns true asleep minutes
 * excluding `.inBed`/`.awake`), otherwise falls back to the aggregated
 * Median bucketed query.
 */
export const getLastNightSleep = async (): Promise<number | null> => {
  if (isKokenHealthAvailable() && window.kokenHealth) {
    try {
      const result = await window.kokenHealth.getSleepLastNight();
      // Return null for "no data" rather than 0 so downstream validators
      // can distinguish "didn't sleep" from "didn't measure".
      if (result.hours <= 0) return null;
      return result.hours;
    } catch (error) {
      console.warn('[KokenHealth] getSleepLastNight error:', error);
      return null;
    }
  }

  if (isKokenHealthAndroidAvailable()) {
    try {
      const result = await callAndroidHealthBridge('getSleepLastNight');
      if (!result || typeof result.hours !== 'number' || result.hours <= 0) return null;
      return result.hours;
    } catch (error) {
      console.warn('[KokenHealthAndroid] getSleepLastNight error:', error);
      return null;
    }
  }

  const sleepData = await getSleepData(1);
  if (!sleepData?.length) return null;

  // Sleep value is typically in minutes, convert to hours
  const lastSleep = sleepData[sleepData.length - 1];
  return lastSleep.value / 60;
};

/**
 * Get last night's full sleep breakdown — hours, bedtime, wake time,
 * per-phase minutes. Only available via the KokenHealthKit native plugin.
 * Returns null on the legacy Median path (which doesn't expose phases).
 */
export const getLastNightSleepDetailed = async (): Promise<KokenHealthSleepLastNight | null> => {
  if (isKokenHealthAvailable() && window.kokenHealth) {
    try {
      return await window.kokenHealth.getSleepLastNight();
    } catch (error) {
      console.warn('[KokenHealth] getSleepLastNight detailed error:', error);
      return null;
    }
  }

  if (isKokenHealthAndroidAvailable()) {
    try {
      return await callAndroidHealthBridge('getSleepLastNight');
    } catch (error) {
      console.warn('[KokenHealthAndroid] getSleepLastNight detailed error:', error);
      return null;
    }
  }

  return null;
};

/**
 * Open `Réglages > Koken`. ⚠️ Ce n'est PAS un chemin de récupération santé :
 * Apple exclut Santé de la page par-app dans Réglages, les interrupteurs ne
 * s'y trouvent pas. Préférer `useHealthPermissionGate().openSettings()`, qui
 * ouvre l'app Santé elle-même (`openHealthApp`, binaires ≥ 2026-08-14) avec
 * repli ici pour les anciens binaires. Helper conservé pour compat, aucun
 * importateur connu.
 *
 * @returns `true` if the deeplink succeeded (on iOS native), `false`
 *   otherwise — e.g. web browser fallback.
 */
export const openHealthSettings = async (): Promise<boolean> => {
  if (isKokenHealthAvailable() && window.kokenHealth?.openHealthSettings) {
    try {
      const result = await window.kokenHealth.openHealthSettings();
      return result?.opened === true;
    } catch (error) {
      console.warn('[KokenHealth] openHealthSettings error:', error);
      return false;
    }
  }
  return false;
};

/**
 * Get today's exercise time in minutes.
 * Routes vers `window.kokenHealth.getExerciseToday()` (appleExerciseTime
 * natif iOS). Fallback Median HealthBridge dormant (non souscrit).
 */
export const getTodayExerciseTime = async (): Promise<number | null> => {
  if (isKokenHealthAvailable() && window.kokenHealth) {
    try {
      const result = await window.kokenHealth.getExerciseToday();
      return result.minutes ?? 0;
    } catch (error) {
      console.warn('[KokenHealth] getExerciseToday error:', error);
      return null;
    }
  }

  if (isKokenHealthAndroidAvailable()) {
    try {
      const result = await callAndroidHealthBridge('getExerciseToday');
      return result?.minutes ?? 0;
    } catch (error) {
      console.warn('[KokenHealthAndroid] getExerciseToday error:', error);
      return null;
    }
  }

  const today = new Date();
  const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  const result = await getHealthData({
    dataTypes: ['exerciseTime'],
    startDate: startOfDay.toISOString(),
    endDate: today.toISOString(),
    bucket: 'day'
  });

  if (!result?.data?.exerciseTime?.length) return null;

  // Sum all exercise data points for today (in minutes)
  return result.data.exerciseTime.reduce((sum, point) => sum + point.value, 0);
};

/**
 * Get weekly exercise time data (last 7 days).
 * Routes to KokenHealthKit's getExerciseRange when available.
 */
export const getWeeklyExerciseTime = async (): Promise<HealthBridgeDataPoint[] | null> => {
  const today = new Date();
  const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

  if (isKokenHealthAvailable() && window.kokenHealth) {
    try {
      const result = await window.kokenHealth.getExerciseRange(
        weekAgo.toISOString(),
        today.toISOString()
      );
      // Adapt KokenHealth's {date, minutes} to Median's {start, end, value}
      return result.points.map(p => {
        const start = new Date(p.date);
        const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);
        return {
          start: start.toISOString(),
          end: end.toISOString(),
          value: p.minutes
        };
      });
    } catch (error) {
      console.warn('[KokenHealth] getExerciseRange error:', error);
      return null;
    }
  }

  if (isKokenHealthAndroidAvailable()) {
    try {
      const result = await callAndroidHealthBridge('getExerciseRange', {
        startDate: weekAgo.toISOString(),
        endDate: today.toISOString()
      });
      return (result?.points || []).map((p: any) => {
        const start = new Date(p.date);
        const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);
        return {
          start: start.toISOString(),
          end: end.toISOString(),
          value: p.minutes
        };
      });
    } catch (error) {
      console.warn('[KokenHealthAndroid] getExerciseRange error:', error);
      return null;
    }
  }

  const result = await getHealthData({
    dataTypes: ['exerciseTime'],
    startDate: weekAgo.toISOString(),
    endDate: today.toISOString(),
    bucket: 'day'
  });

  return result?.data?.exerciseTime || null;
};

/**
 * Get today's distance in kilometers.
 * Routes to KokenHealthKit when available, otherwise falls back to Median.
 */
export const getTodayDistance = async (): Promise<number | null> => {
  if (isKokenHealthAvailable() && window.kokenHealth) {
    try {
      const result = await window.kokenHealth.getDistanceToday();
      return result.kilometers ?? 0;
    } catch (error) {
      console.warn('[KokenHealth] getDistanceToday error:', error);
      return null;
    }
  }

  if (isKokenHealthAndroidAvailable()) {
    try {
      const result = await callAndroidHealthBridge('getDistanceToday');
      return result?.kilometers ?? 0;
    } catch (error) {
      console.warn('[KokenHealthAndroid] getDistanceToday error:', error);
      return null;
    }
  }

  const today = new Date();
  const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  const result = await getHealthData({
    dataTypes: ['distance'],
    startDate: startOfDay.toISOString(),
    endDate: today.toISOString(),
    bucket: 'day'
  });

  if (!result?.data?.distance?.length) return null;

  // Sum all distance data points for today (in km)
  // Health Bridge returns meters, convert to km
  const totalMeters = result.data.distance.reduce((sum, point) => sum + point.value, 0);
  return totalMeters / 1000;
};

/**
 * Get weekly distance data (last 7 days).
 * Routes vers `window.kokenHealth.getDistanceRange()`
 * (HKQuantityType.distanceWalkingRunning, plugin iOS natif). Fallback
 * Median HealthBridge dormant (plan payant non souscrit chez Koken).
 *
 * Each data point is in METERS (to match the legacy shape consumers
 * expect — `getTodayDistance` divides to km but the range stays in
 * meters so `usePhoneSyncValidation.sumWeekly` can sum them cleanly).
 */
export const getWeeklyDistance = async (): Promise<HealthBridgeDataPoint[] | null> => {
  const today = new Date();
  const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

  if (isKokenHealthAvailable() && window.kokenHealth) {
    try {
      const result = await window.kokenHealth.getDistanceRange(
        weekAgo.toISOString(),
        today.toISOString()
      );
      // Return distance in KM (matches the threshold unit used in
      // usePhoneSyncValidation.checkGoalValidation for distance goals —
      // `running-5k` → 5 km, `hiking-10k` → 10 km, `marathon-42k` → 42 km).
      return result.points.map(p => {
        const start = new Date(p.date);
        const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);
        return {
          start: start.toISOString(),
          end: end.toISOString(),
          value: p.kilometers,
        };
      });
    } catch (error) {
      console.warn('[KokenHealth] getDistanceRange error:', error);
      return null;
    }
  }

  if (isKokenHealthAndroidAvailable()) {
    try {
      const result = await callAndroidHealthBridge('getDistanceRange', {
        startDate: weekAgo.toISOString(),
        endDate: today.toISOString()
      });
      return (result?.points || []).map((p: any) => {
        const start = new Date(p.date);
        const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);
        return {
          start: start.toISOString(),
          end: end.toISOString(),
          value: p.kilometers,
        };
      });
    } catch (error) {
      console.warn('[KokenHealthAndroid] getDistanceRange error:', error);
      return null;
    }
  }

  const result = await getHealthData({
    dataTypes: ['distance'],
    startDate: weekAgo.toISOString(),
    endDate: today.toISOString(),
    bucket: 'day'
  });

  return result?.data?.distance || null;
};

export const updateHealthGoalSubscriptions = async (
  subscriptions: HealthGoalSubscription[]
): Promise<boolean> => {
  if (!isKokenHealthAvailable() || !window.kokenHealth?.updateGoalSubscriptions) {
    return false;
  }

  try {
    const result = await window.kokenHealth.updateGoalSubscriptions(subscriptions);
    return result?.success === true;
  } catch (error) {
    console.warn('[KokenHealth] updateGoalSubscriptions error:', error);
    return false;
  }
};

export const clearHealthGoalSubscriptions = async (): Promise<boolean> => {
  if (!isKokenHealthAvailable() || !window.kokenHealth?.clearGoalSubscriptions) {
    return false;
  }

  try {
    const result = await window.kokenHealth.clearGoalSubscriptions();
    return result?.success === true;
  } catch (error) {
    console.warn('[KokenHealth] clearGoalSubscriptions error:', error);
    return false;
  }
};

export const getHealthBackgroundStatus = async (): Promise<KokenHealthBackgroundStatus | null> => {
  if (!isKokenHealthAvailable() || !window.kokenHealth?.getHealthBackgroundStatus) {
    return null;
  }

  try {
    return await window.kokenHealth.getHealthBackgroundStatus();
  } catch (error) {
    console.warn('[KokenHealth] getHealthBackgroundStatus error:', error);
    return null;
  }
};

// ============== Native auth bridge (bg-sync serveur, Option A) ==============
//
// Le background observer iOS (`KokenHealthKitBackground.swift`) ne peut PAS lire
// le `localStorage` de la WebView quand l'app est fermée. Pour que les objectifs
// s'auto-valident côté serveur sans ouvrir l'app, on pousse le `refresh_token`
// Supabase dans le Keychain natif. Le natif rafraîchit alors un `access_token`
// court et POST les samples HealthKit à `persist-health-data`.
//
// Sécurité : le `refresh_token` ne quitte jamais l'appareil côté JS — il passe
// uniquement par le bridge in-app (`window.kokenHealth.updateAuth`) vers le shell
// natif, qui le stocke chiffré (Keychain, accessible après le premier déverrouillage).

/** Forme minimale d'une session Supabase nécessaire au pont natif. */
type NativeAuthSession = { refresh_token?: string | null } | null | undefined;

// Mêmes constantes que `src/integrations/supabase/client.ts` — on lit l'env Vite
// au lieu de hardcoder une nouvelle valeur.
const SUPABASE_URL_FOR_NATIVE = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const SUPABASE_ANON_KEY_FOR_NATIVE = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;

/**
 * Pousse un message best-effort au bridge Android `window.kokenHealthAndroid`
 * en réutilisant EXACTEMENT l'enveloppe postMessage déjà utilisée dans ce
 * fichier (cf. `callAndroidHealthBridge` : `{ method, callbackId, params }`).
 *
 * Fire-and-forget volontaire : le shell Android traite le message dès réception
 * (stockage EncryptedSharedPreferences), et la valeur de retour `updateAuth` /
 * `clearAuth` n'est jamais attendue côté appelant (`void pushAuthToNative`). On
 * ne dépend donc pas de la réponse native — on signale simplement que le message
 * a bien été posté. No-op silencieux si le bridge Android est absent (iOS/PWA).
 *
 * ⚠️ Ne jamais logger le token. Sécurité identique au chemin iOS : le
 * `refresh_token` ne transite QUE par le bridge in-app vers le shell natif.
 */
const postAndroidHealthAuth = (method: 'updateAuth' | 'clearAuth', params: Record<string, unknown>): boolean => {
  if (!isKokenHealthAndroidAvailable()) return false;
  const win = window as unknown as { kokenHealthAndroid?: { postMessage?: (msg: string) => void } };
  const bridge = win.kokenHealthAndroid;
  if (!bridge?.postMessage) return false;

  try {
    bridge.postMessage(JSON.stringify({
      method,
      callbackId: `health_${method}_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
      params,
    }));
    return true;
  } catch (error) {
    // Ne jamais logger le token. On ne logge que l'échec.
    console.warn(`[KokenHealthAndroid] ${method} postMessage error (non-blocking)`);
    return false;
  }
};

/**
 * Pousse les credentials Supabase (refresh_token + url + anonKey) au plugin natif
 * pour permettre le server-sync background (Option A). Couvre iOS
 * (`window.kokenHealth.updateAuth`) ET Android (`window.kokenHealthAndroid`
 * postMessage). No-op silencieux si :
 *   - aucun bridge natif n'est disponible (web/PWA, ou plugin absent),
 *   - la méthode `updateAuth` n'est pas exposée (vieille version du shell natif),
 *   - la session n'a pas de `refresh_token`, ou l'URL/anonKey ne sont pas configurés.
 *
 * @returns `true` si un bridge natif a accepté les credentials, `false` sinon
 *          (toujours non-bloquant).
 */
export const pushAuthToNative = async (session: NativeAuthSession): Promise<boolean> => {
  const refreshToken = session?.refresh_token ?? undefined;
  if (!refreshToken || !SUPABASE_URL_FOR_NATIVE || !SUPABASE_ANON_KEY_FOR_NATIVE) {
    return false;
  }

  const authArgs = {
    refreshToken,
    supabaseUrl: SUPABASE_URL_FOR_NATIVE,
    anonKey: SUPABASE_ANON_KEY_FOR_NATIVE,
  };

  // iOS — bridge custom KokenHealthKit (window.kokenHealth)
  if (isKokenHealthAvailable() && window.kokenHealth?.updateAuth) {
    try {
      const result = await window.kokenHealth.updateAuth(authArgs);
      return result?.success === true;
    } catch (error) {
      // Ne jamais logger le token. On ne logge que l'échec.
      console.warn('[KokenHealth] updateAuth error (non-blocking)');
      return false;
    }
  }

  // Android — bridge custom postMessage (window.kokenHealthAndroid)
  return postAndroidHealthAuth('updateAuth', authArgs);
};

/**
 * Efface les credentials stockés côté natif (au SIGNED_OUT). Couvre iOS
 * (`window.kokenHealth.clearAuth`) ET Android (`window.kokenHealthAndroid`
 * postMessage). No-op silencieux si aucun bridge n'est disponible. Toujours
 * non-bloquant.
 */
export const clearNativeAuth = async (): Promise<boolean> => {
  // iOS — bridge custom KokenHealthKit (window.kokenHealth)
  if (isKokenHealthAvailable() && window.kokenHealth?.clearAuth) {
    try {
      const result = await window.kokenHealth.clearAuth();
      return result?.success === true;
    } catch (error) {
      console.warn('[KokenHealth] clearAuth error (non-blocking)');
      return false;
    }
  }

  // Android — bridge custom postMessage (window.kokenHealthAndroid)
  return postAndroidHealthAuth('clearAuth', {});
};

/**
 * Lit le `refresh_token` Supabase VIVANT depuis le store natif (Keychain iOS via
 * `window.kokenHealth.getAuth`). Sert UNIQUEMENT à récupérer une session après
 * un échec de refresh local : le background observer iOS rote le token app
 * fermée et l'écrit dans le Keychain, pas dans le `localStorage` du WebView, donc
 * au réveil le `localStorage` peut être périmé.
 *
 * iOS uniquement : le bridge Android (`window.kokenHealthAndroid`, postMessage
 * unidirectionnel) ne sait pas renvoyer de valeur, et aucun worker Android ne
 * rote le token → pas de récupération nécessaire côté Android. Renvoie `null`
 * hors iOS natif, si la méthode n'existe pas (vieux shell), ou si aucun token
 * n'est stocké. Ne logge JAMAIS le token.
 *
 * @returns `{ refreshToken }` si un token natif existe, sinon `null`.
 */
export const getAuthFromNative = async (): Promise<{ refreshToken: string } | null> => {
  if (isKokenHealthAvailable() && window.kokenHealth?.getAuth) {
    try {
      const result = await window.kokenHealth.getAuth();
      const token = result?.refreshToken;
      return token ? { refreshToken: token } : null;
    } catch {
      // Ne jamais logger le token. On ne logge que l'échec.
      console.warn('[KokenHealth] getAuth error (non-blocking)');
      return null;
    }
  }
  return null;
};

// ============== Theme Sync ==============

/**
 * Convert a CSS HSL value string (e.g. "222 47% 11%") to a hex colour.
 * Returns null when parsing fails so callers can fall back to a default.
 */
export const hslToHex = (hslString: string): string | null => {
  if (!hslString) return null;
  const parts = hslString.replace(/,/g, ' ').trim().split(/\s+/);
  if (parts.length < 3) return null;

  const h = parseFloat(parts[0]) / 360;
  const s = parseFloat(parts[1]) / 100;
  const l = parseFloat(parts[2]) / 100;

  if (isNaN(h) || isNaN(s) || isNaN(l)) return null;

  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  let r: number, g: number, b: number;
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  const toHex = (c: number) => Math.round(c * 255).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Sync app theme with native status/nav bars.
 *
 * Reads the actual --background CSS variable so that custom themes
 * (champagne-gold, etc.) are reflected in the iOS status bar and
 * Android navigation bar — not just a hardcoded black/white.
 */
export const syncNativeTheme = (isDark: boolean): void => {
  if (!isMedianNative()) return;

  // Read the real background colour from the active CSS theme
  const bgVar = getComputedStyle(document.documentElement)
    .getPropertyValue('--background').trim();
  const hexBg = hslToHex(bgVar) || (isDark ? '#0C091A' : '#FFFFFF');

  if (isDark) {
    setStatusBar({ style: 'dark', color: hexBg, overlay: false });
    setNavigationBar({ color: hexBg, lightIcons: true });
  } else {
    setStatusBar({ style: 'light', color: hexBg, overlay: false });
    setNavigationBar({ color: hexBg, lightIcons: false });
  }
};

// ============== Init ==============

/**
 * Initialize Median bridge and sync theme
 */
export const initMedianBridge = (): void => {
  if (!isMedianNative()) {
    console.log('[MedianBridge] Not running in Median native wrapper');
    return;
  }

  console.log('[MedianBridge] Initialized', {
    platform: getMedianPlatform(),
    hasStatusBar: !!window.median?.statusbar,
    hasNavigationBar: !!window.median?.navigationbar,
    hasOneSignal: !!window.median?.onesignal,
    hasHaptics: !!window.median?.haptics,
    hasHealthBridge: !!window.median?.healthBridge
  });

  // Initialize push notification click handler for deep linking
  initPushClickHandler();

  const runInitialThemeSync = () => {
    const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    syncNativeTheme(isDark);
  };

  // Wait for the Median API injection; without it, setStatusBar is a silent no-op.
  if (window.median?.statusbar) {
    runInitialThemeSync();
  } else {
    document.addEventListener('median_library_ready', runInitialThemeSync, { once: true });
  }
};

// ============== App Exit ==============

/**
 * Exit the Median app (Android only)
 * Uses history manipulation to trigger app exit
 * On iOS, apps cannot programmatically exit (against Apple guidelines)
 */
export const exitMedianApp = (): void => {
  if (!isMedianNative()) {
    console.warn('[MedianBridge] Not in native context, cannot exit');
    return;
  }

  // On Android, go back until app exits
  // Median WebView will handle the actual exit when history is exhausted
  if (isMedianAndroid()) {
    // Clear history and go back to trigger exit
    window.history.go(-(window.history.length - 1));
    // Fallback: try to close window (might work in some WebView configs)
    setTimeout(() => {
      window.close();
    }, 100);
  } else {
    // On iOS, we can't programmatically exit
    // The best we can do is navigate to home
    console.warn('[MedianBridge] App exit not available on iOS');
  }
};

// ==========================================================================
// Additional KokenHealthKit wrappers — today-only metrics (no range support
// on the native side yet: activeEnergy, restingHR, mindful, stand, flights).
// Lot 3a « santé zéro conservation » : ces valeurs restent SUR L'APPAREIL —
// il n'existe plus aucune table serveur pouvant les contenir. Le range
// multi-jours viendra des méthodes natives Range du lot 3, jamais du serveur.
// ==========================================================================

/**
 * Get today's active energy (calories burned) in kcal.
 * Routes to KokenHealthKit's getActiveEnergyToday when available.
 * Returns null if bridge unavailable or permission denied.
 */
export const getTodayActiveEnergy = async (): Promise<number | null> => {
  if (isKokenHealthAvailable() && window.kokenHealth?.getActiveEnergyToday) {
    try {
      const result = await window.kokenHealth.getActiveEnergyToday();
      return (result as KokenHealthEnergyToday).kilocalories ?? 0;
    } catch (error) {
      console.warn('[KokenHealth] getActiveEnergyToday error:', error);
      return null;
    }
  }
  return null;
};

/**
 * Get today's flights climbed (étages montés).
 * Routes to KokenHealthKit's getFlightsClimbedToday when available.
 */
export const getTodayFlightsClimbed = async (): Promise<number | null> => {
  if (isKokenHealthAvailable() && window.kokenHealth?.getFlightsClimbedToday) {
    try {
      const result = await window.kokenHealth.getFlightsClimbedToday();
      return (result as KokenHealthFlightsToday).flights ?? 0;
    } catch (error) {
      console.warn('[KokenHealth] getFlightsClimbedToday error:', error);
      return null;
    }
  }
  return null;
};

/**
 * Get today's mindful minutes (méditation via Apple Health sessions).
 */
export const getTodayMindfulMinutes = async (): Promise<number | null> => {
  if (isKokenHealthAvailable() && window.kokenHealth?.getMindfulMinutesToday) {
    try {
      const result = await window.kokenHealth.getMindfulMinutesToday();
      return (result as KokenHealthMindfulToday).minutes ?? 0;
    } catch (error) {
      console.warn('[KokenHealth] getMindfulMinutesToday error:', error);
      return null;
    }
  }
  return null;
};

/**
 * Get today's stand minutes (Apple Watch only).
 */
export const getTodayStandMinutes = async (): Promise<number | null> => {
  if (isKokenHealthAvailable() && window.kokenHealth?.getStandMinutesToday) {
    try {
      const result = await window.kokenHealth.getStandMinutesToday();
      return (result as KokenHealthStandToday).minutes ?? 0;
    } catch (error) {
      console.warn('[KokenHealth] getStandMinutesToday error:', error);
      return null;
    }
  }
  return null;
};

/**
 * Get the latest heart rate measurement (bpm).
 * Only Apple Watch data — iPhone doesn't record heart rate.
 */
export const getLatestHeartRate = async (): Promise<number | null> => {
  if (isKokenHealthAvailable() && window.kokenHealth?.getHeartRateLatest) {
    try {
      const result = await window.kokenHealth.getHeartRateLatest();
      return (result as KokenHealthHeartRateLatest).bpm ?? null;
    } catch (error) {
      console.warn('[KokenHealth] getHeartRateLatest error:', error);
      return null;
    }
  }
  return null;
};

/**
 * Get today's resting heart rate average (bpm).
 * Apple Watch only, requires at least a few hours of wear.
 */
export const getTodayRestingHeartRate = async (): Promise<number | null> => {
  if (isKokenHealthAvailable() && window.kokenHealth?.getRestingHeartRateToday) {
    try {
      const result = await window.kokenHealth.getRestingHeartRateToday();
      return (result as KokenHealthRestingHeartRate).bpm ?? null;
    } catch (error) {
      console.warn('[KokenHealth] getRestingHeartRateToday error:', error);
      return null;
    }
  }
  return null;
};

// (getLastNightSleepDetailed already declared above at line ~1002)

// ==========================================================================
// Range wrappers for the iOS plugin v2 (added 2026-04-18) :
// activeEnergy, flightsClimbed, mindful, restingHeartRate now support 7j
// bucketed reads. Used by useHealthKitAll for the weekly chart filling.
// ==========================================================================

/**
 * Get active energy burned (kcal) per day over a range.
 * Returns [] if bridge unavailable or range method not shipped yet.
 */
export const getWeeklyActiveEnergy = async (): Promise<HealthBridgeDataPoint[] | null> => {
  if (!isKokenHealthAvailable() || !window.kokenHealth?.getActiveEnergyRange) return null;
  try {
    const today = new Date();
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const result = await window.kokenHealth.getActiveEnergyRange(
      weekAgo.toISOString(),
      today.toISOString(),
    );
    return (result.points as Array<{ date: string; kilocalories: number }>).map((p) => {
      const start = new Date(p.date);
      const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);
      return { start: start.toISOString(), end: end.toISOString(), value: p.kilocalories };
    });
  } catch (error) {
    console.warn('[KokenHealth] getActiveEnergyRange error:', error);
    return null;
  }
};

/** Flights climbed per day over last 7d. */
export const getWeeklyFlightsClimbed = async (): Promise<HealthBridgeDataPoint[] | null> => {
  if (!isKokenHealthAvailable() || !window.kokenHealth?.getFlightsClimbedRange) return null;
  try {
    const today = new Date();
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const result = await window.kokenHealth.getFlightsClimbedRange(
      weekAgo.toISOString(),
      today.toISOString(),
    );
    return (result.points as Array<{ date: string; flights: number }>).map((p) => {
      const start = new Date(p.date);
      const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);
      return { start: start.toISOString(), end: end.toISOString(), value: p.flights };
    });
  } catch (error) {
    console.warn('[KokenHealth] getFlightsClimbedRange error:', error);
    return null;
  }
};

/** Mindful minutes per day over last 7d. */
export const getWeeklyMindfulMinutes = async (): Promise<HealthBridgeDataPoint[] | null> => {
  if (!isKokenHealthAvailable() || !window.kokenHealth?.getMindfulMinutesRange) return null;
  try {
    const today = new Date();
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const result = await window.kokenHealth.getMindfulMinutesRange(
      weekAgo.toISOString(),
      today.toISOString(),
    );
    return (result.points as Array<{ date: string; minutes: number }>).map((p) => {
      const start = new Date(p.date);
      const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);
      return { start: start.toISOString(), end: end.toISOString(), value: p.minutes };
    });
  } catch (error) {
    console.warn('[KokenHealth] getMindfulMinutesRange error:', error);
    return null;
  }
};

/** Resting heart rate average per day over last 7d (Apple Watch only). */
export const getWeeklyRestingHeartRate = async (): Promise<HealthBridgeDataPoint[] | null> => {
  if (!isKokenHealthAvailable() || !window.kokenHealth?.getRestingHeartRateRange) return null;
  try {
    const today = new Date();
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const result = await window.kokenHealth.getRestingHeartRateRange(
      weekAgo.toISOString(),
      today.toISOString(),
    );
    return (result.points as Array<{ date: string; bpm: number }>).map((p) => {
      const start = new Date(p.date);
      const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);
      return { start: start.toISOString(), end: end.toISOString(), value: p.bpm };
    });
  } catch (error) {
    console.warn('[KokenHealth] getRestingHeartRateRange error:', error);
    return null;
  }
};

export default {
  // Detection
  isMedianNative,
  getMedianPlatform,
  isMedianIOS,
  isMedianAndroid,
  isHealthBridgeAvailable,
  isKokenHealthAvailable,
  getKokenHealthCapabilities,
  // UI
  setStatusBar,
  setNavigationBar,
  triggerMedianHaptic,
  // Push
  registerPushNotifications,
  getOneSignalInfo,
  loginOneSignal,
  logoutOneSignal,
  setOneSignalExternalUserId,
  setOneSignalTags,
  initPushClickHandler,
  // Share
  shareNative,
  // Health Bridge
  requestHealthPermissions,
  getHealthData,
  getTodaySteps,
  getWeeklySteps,
  getSleepData,
  getLastNightSleep,
  getTodayExerciseTime,
  getWeeklyExerciseTime,
  getTodayDistance,
  getWeeklyDistance,
  getTodayActiveEnergy,
  getTodayFlightsClimbed,
  getTodayMindfulMinutes,
  getTodayStandMinutes,
  getLatestHeartRate,
  getTodayRestingHeartRate,
  getLastNightSleepDetailed,
  updateHealthGoalSubscriptions,
  clearHealthGoalSubscriptions,
  getHealthBackgroundStatus,
  openHealthSettings,
  // Theme
  syncNativeTheme,
  initMedianBridge,
  // App Exit
  exitMedianApp
};
